import { useEffect, useState } from 'react';
import { IconAccordionCaretDown, IconButton } from '@lmig/lmds-react-icons';
import { Popover } from '@lmig/lmds-react-popover';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import { StringHelper } from 'Utilities/StringHelper';
import { useUserData } from 'Foundation/User/UserContext';
import './AgencyCodeSelector.scss';

export function PLDropdown() {
	const { userData, updateSelectedStat } = useUserData();
	const [searchValue, setSearchValue] = useState<string>('');
	const [statCodes, setStatCodes] = useState<Agency[]>([]);
	const [selectedStatCode, setSelectedStatCode] = useState<string>('');
	const [enabled, setIsenabled] = useState<boolean>(false);
	const [filteredCodes, setFilteredCodes] = useState<Agency[]>();
	const [isOpen, setIsOpen] = useState<boolean>(false);

	useEffect(() => {
		if (searchValue && statCodes) {
			const filterValue = searchValue.replaceAll('-', '');
			setFilteredCodes(statCodes.filter((code) => code.AgencyNumber.replaceAll('-', '').indexOf(filterValue) > -1));
		} else {
			setFilteredCodes(undefined);
		}
	}, [searchValue, statCodes]);

	useEffect(() => {
		if (statCodes && statCodes.length > 1) setIsenabled(true);
	}, [statCodes]);

	useEffect(() => {
		async function getAgencies() {
			let res = await fetch('/api/sitecore/Agency/GetAgencies');
			let data = await res.json();
			if (data && data.length > 0)
				setStatCodes(
					data
						.filter((y: MasterAgency) => y.Subs.length > 0 || y.HasAccess)
						.flatMap((x: MasterAgency) => [{ AgencyNumber: x.AgencyNumber, HasAccess: x.HasAccess }, ...x.Subs])
						.sort((w: Agency, z: Agency) => parseInt(w.AgencyNumber) - parseInt(z.AgencyNumber))
						.map((x: Agency) => ({
							AgencyNumber: StringHelper.formatStatCode(x.AgencyNumber),
							HasAccess: x.HasAccess != undefined ? x.HasAccess : true,
						}))
						.filter((z: Agency) => z.HasAccess),
				);
		}
		if (statCodes.length === 0) getAgencies();
	}, []);

	useEffect(() => {
		if (userData?.selectedStatCodeData.SubStat)
			setSelectedStatCode(StringHelper.formatStatCode(userData.selectedStatCodeData.SubStat));
	}, [userData?.selectedStatCodeData.SubStat]);

	const showFilter = () => statCodes.length >= 4;
	const onStatSelected = async (event: React.MouseEvent<HTMLElement>, formattedSubStat: string) => {
		event.stopPropagation();
		setSelectedStatCode(formattedSubStat);
		setIsOpen(false);

		try {
			await updateSelectedStat(formattedSubStat);
			window.location.reload();
		} catch {
			console.error("Error saving user's agency code");
		}
	};
	const highlight = (agencyCode: string) => {
		if (searchValue) {
			const filterValue = searchValue.replaceAll('-', '');
			const match = agencyCode.match(
				filterValue
					.split('')
					.map((value) => value + '-?')
					.join(''),
			);

			if (match) {
				const index = match.index ?? 0;
				const length = match[0].length ?? 0;

				return (
					<span>
						{agencyCode.substring(0, index)}
						<b>{agencyCode.substring(index, index + length)}</b>
						{agencyCode.substring(index + length)}
					</span>
				);
			}
		}

		return <span>{agencyCode}</span>;
	};
	const onOpen = () => enabled && setIsOpen(true);
	const onClose = () => setIsOpen(false);
	return (
		<div className="top-nav-dropdown">
			<Popover
				onOpen={() => onOpen()}
				onClose={() => onClose()}
				trigger={
					<IconButton background="transparent" className={enabled ? '' : 'agency-code-disabled'}>
						<div className="icon-button-label">
							<span>{selectedStatCode}</span>
							{enabled && <IconAccordionCaretDown title="" />}
						</div>
					</IconButton>
				}
				variant="custom"
				direction="below"
				align="start"
				className={isOpen ? 'open' : 'closed'}
			>
				<div className="popover-content">
					{showFilter() && (
						<SearchInput
							id="agency-code-filter"
							labelVisual="Filter codes"
							value={searchValue}
							filter={new RegExp('^(?:\\d[-]*){0,8}$')}
							onChange={(e) => setSearchValue(e.target.value)}
							aria-live="polite"
							onClearClicked={() => setSearchValue('')}
						/>
					)}
					<ul>
						{(filteredCodes ?? statCodes)?.map((agency) => (
							<li key={agency.AgencyNumber} onClick={(event) => agency.HasAccess && onStatSelected(event, agency.AgencyNumber)}>
								<div className={`master-code ${agency.HasAccess ? '' : 'no-access'}`}>
									<span>{highlight(agency.AgencyNumber)}</span>
								</div>
							</li>
						))}
					</ul>
					{filteredCodes && filteredCodes.length === 0 && <p className="no-matching-agency-codes">No matching agency codes</p>}
				</div>
			</Popover>
		</div>
	);
}
